import React, { useMemo } from 'react';
import { Btn, DataTable } from '../../kit';
import { AdminLink } from '../../adminNav';
import { CtaCell, CtaClicks, ModePill, Pct, arr, categoryWord, num, rateOf, str } from './shared';

/**
 * By template (email/stats_overview → by_template): every e-mail sent in the
 * period, most sent first, with its primary button and its rates. The best
 * three open and click rates are marked 1 / 2 / 3 — among templates delivered
 * to at least RANK_MIN people, so a mail that reached three people and was
 * opened by two does not top the list.
 */

const RANK_MIN = 20;
/** template × mode, and per campaign for one written for a single send. */
const rowId = (r: any) => `${str(r.template_key)}|${str(r.mode)}|${str(r.send_id)}`;
const MARK = ['①', '②', '③'];

const TemplateStats: React.FC<{
  rows: any[]; tplName: (k: string) => string;
  active?: string; onTemplate: (k: string) => void; onSends: (k: string) => void;
}> = ({ rows, tplName, active, onTemplate, onSends }) => {
  const view = useMemo(() => arr(rows).filter(Boolean).map(r => ({
    ...r,
    name: str(r.template_name) || str(r.name) || tplName(str(r.template_key)),
    category_label: categoryWord(r.category),
    open_rate: rateOf(r, 'open_rate'),
    click_rate: rateOf(r, 'click_rate'),
    ctor: rateOf(r, 'ctor'),
  })), [rows, tplName]);
  const ranks = useMemo(() => {
    const out: Record<string, Record<string, number>> = { open_rate: {}, click_rate: {} };
    (['open_rate', 'click_rate'] as const).forEach(k => {
      view.filter(r => num(r.delivered) >= RANK_MIN && typeof r[k] === 'number' && r[k] > 0)
        .sort((a, b) => b[k] - a[k]).slice(0, 3)
        .forEach((r, i) => { out[k][rowId(r)] = i; });
    });
    return out;
  }, [view]);
  const ranked = (k: 'open_rate' | 'click_rate') => (r: any) => {
    const i = ranks[k][rowId(r)];
    return (
      <span className="inline-flex items-center gap-1">
        <Pct v={r[k]} strong={i !== undefined} />
        {i !== undefined && <span className="text-primary-700" title={`${i + 1}${['st', 'nd', 'rd'][i]} best in this period (${RANK_MIN}+ delivered)`}>{MARK[i]}</span>}
      </span>
    );
  };
  return (
    <div className="flex flex-col gap-2">
      <DataTable rows={view} csvName="email_by_template" tableId="email_dash_by_template" defaultSort="-sent"
        maxHeight="36rem"
        columns={['name', 'category_label', 'primary_cta', 'mode', 'sent', 'delivered', 'opened', 'clicked',
          'cta_clicks', 'open_rate', 'click_rate', 'ctor', 'auto_opened', 'bot_clicked']}
        optionalColumns={['template_key', 'trigger_id', 'bounced', 'failed', 'unsubscribed', 'last_sent_at', 'html']}
        labels={{
          name: 'E-mail', category_label: 'Category', primary_cta: 'Primary CTA', cta_clicks: 'CTA clicks',
          open_rate: 'Open rate', click_rate: 'Click rate', ctor: 'CTOR', template_key: 'Template key', html: 'HTML',
          trigger_id: 'MEMAC trigger', last_sent_at: 'Last sent', auto_opened: 'Auto opens', bot_clicked: 'Bot clicks',
        }}
        render={{
          name: r => (
            <span className="inline-flex items-center gap-1">
              <button type="button" onClick={() => onTemplate(str(r.template_key))}
                title={`${r.template_key} — click to narrow the dashboard to this e-mail`}
                className={`text-left hover:underline underline-offset-2 ${active === r.template_key ? 'font-bold' : 'font-medium'}`}>
                {r.name}
              </button>
              {r.send_id && (
                <AdminLink to={{ group: 'email', tab: 'campaigns', campaign: str(r.send_id) }}
                  title="Open this campaign in Campaigns"
                  className="text-[11px] text-primary-700 underline underline-offset-2">campaign</AdminLink>
              )}
            </span>
          ),
          cta_clicks: r => <CtaClicks row={r} />,
          primary_cta: r => <CtaCell row={r} />,
          mode: r => <ModePill mode={r.mode} />,
          open_rate: ranked('open_rate'),
          click_rate: ranked('click_rate'),
          ctor: r => <Pct v={r.ctor} />,
          auto_opened: r => (num(r.auto_opened)
            ? <span className="text-amber-800" title="Sends opened only by machines: instant reads, Apple MPP, scanners">{num(r.auto_opened)}</span>
            : <span className="text-grayscale-1100">0</span>),
          bot_clicked: r => (num(r.bot_clicked)
            ? <span className="text-red-700" title="Sends clicked only by bots (link scanners)">{num(r.bot_clicked)}</span>
            : <span className="text-grayscale-1100">0</span>),
        }}
        actions={r => <Btn small onClick={() => onSends(str(r.template_key))} title="Every send of this e-mail">Sends</Btn>}
        empty="Nothing was sent in this period with these filters." />
      <div className="text-[11px] text-grayscale-1100">
        ①②③ = the best three open and click rates among e-mails delivered to {RANK_MIN} or more people.
        Click a name to narrow the whole dashboard to that e-mail. Auto opens = sends opened only by machines
        (instant reads, Apple MPP, scanners); bot clicks = sends clicked only by link scanners.
      </div>
    </div>
  );
};

export default TemplateStats;
